import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { FaArrowLeft, FaMountain } from 'react-icons/fa';

const BRAND = { blue: '#043663', red: '#D52B1E' };

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Wrap = styled.div`
  max-width: 720px;
  min-height: min(65vh, 520px);
  animation: ${fadeUp} 0.3s ease-out;
`;

const BackBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  border: 1px solid #e2e8f0;
  background: #fff;
  color: ${BRAND.blue};
  font-weight: 600;
  font-size: 0.8125rem;
  padding: 0.45rem 0.85rem;
  margin: 0 0 1.1rem 0;
  border-radius: 10px;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  &:hover {
    border-color: rgba(4, 54, 99, 0.28);
    background: #f8fafc;
  }
`;

const Panel = styled.div`
  padding: 2.25rem 1.5rem 2.4rem;
  border-radius: 16px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid rgba(15, 23, 42, 0.08);
  box-shadow:
    0 1px 2px rgba(15, 23, 42, 0.04),
    0 12px 28px rgba(15, 23, 42, 0.05);
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Icon = styled.div`
  width: 3.4rem;
  height: 3.4rem;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  background: linear-gradient(145deg, #e2e8f0, #f8fafc);
  color: ${BRAND.blue};
  font-size: 1.35rem;
  border: 1px solid #e2e8f0;
`;

const Badge = styled.span`
  margin-bottom: 0.75rem;
  font-size: 0.65rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #0f766e;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  padding: 0.28rem 0.55rem;
  border-radius: 999px;
`;

const Title = styled.h2`
  margin: 0 0 0.5rem 0;
  font-size: clamp(1.35rem, 3vw, 1.6rem);
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.03em;
`;

const Hint = styled.p`
  margin: 0 0 1.35rem;
  max-width: 28rem;
  color: #64748b;
  font-size: 0.92rem;
  line-height: 1.55;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
  justify-content: center;
`;

const GhostBtn = styled.button`
  border: 1px solid #e2e8f0;
  background: #fff;
  color: ${BRAND.blue};
  font-weight: 700;
  font-size: 0.82rem;
  padding: 0.55rem 0.95rem;
  border-radius: 10px;
  cursor: pointer;
  &:hover {
    border-color: rgba(4, 54, 99, 0.3);
  }
`;

const PrimaryBtn = styled.button`
  border: none;
  background: ${BRAND.red};
  color: #fff;
  font-weight: 700;
  font-size: 0.82rem;
  padding: 0.55rem 0.95rem;
  border-radius: 10px;
  cursor: pointer;
  &:hover {
    background: #b91c1c;
  }
`;

export default function PlayerEventsCampsPlaceholder() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <Wrap>
      <BackBtn type="button" onClick={() => navigate('/player/tournaments')}>
        <FaArrowLeft size={12} aria-hidden />
        {t('playerOrganisedEvents.backToEvents', 'Back to Events')}
      </BackBtn>
      <Panel>
        <Icon aria-hidden>
          <FaMountain />
        </Icon>
        <Badge>{t('playerOrganisedEvents.comingSoon', 'Coming soon')}</Badge>
        <Title>{t('playerOrganisedEvents.campsTitle', 'Camps')}</Title>
        <Hint>
          {t(
            'playerOrganisedEvents.campsPlaceholder',
            'Camps will appear here when HPS publishes the first one. Navigation stays under Events — no redesign needed.'
          )}
        </Hint>
        <Actions>
          <GhostBtn type="button" onClick={() => navigate('/player/tournaments')}>
            {t('playerOrganisedEvents.backToEvents', 'Back to Events')}
          </GhostBtn>
          <PrimaryBtn type="button" onClick={() => navigate('/player/tournaments?area=leagues')}>
            {t('playerOrganisedEvents.goToLeagues', 'Go to Leagues')}
          </PrimaryBtn>
        </Actions>
      </Panel>
    </Wrap>
  );
}
